import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface AnchoredPopoverProps {
  anchor: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  width?: number;
  ariaLabel: string;
  className?: string;
  children: React.ReactNode;
}

const GAP = 6;
const MARGIN = 8;

/**
 * A small popover pinned under a grid cell or header. Portalled to <body> with fixed positioning so
 * the grids' scroll wrappers can never clip it; it follows the anchor on scroll/resize and closes on
 * Escape or an outside click (a click on the anchor itself is left to the anchor's own handler).
 */
const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  anchor,
  open,
  onClose,
  width = 300,
  ariaLabel,
  className = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const place = useCallback(() => {
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || width;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const centred = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(MARGIN, Math.min(centred, viewportWidth - width - MARGIN));
    // Below the anchor by default. A fixed element can never be scrolled into view, so when there
    // is no room below (a header or cell at the bottom edge) open ABOVE it, or failing that pin it
    // inside the viewport — never off-screen.
    const height = panelRef.current?.offsetHeight ?? 0;
    let top = rect.bottom + GAP;
    if (viewportHeight > 0 && top + height > viewportHeight - MARGIN) {
      const above = rect.top - GAP - height;
      top = above >= MARGIN ? above : Math.max(MARGIN, viewportHeight - height - MARGIN);
    }
    setPosition({ top, left });
  }, [anchor, width]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchor, onClose, place]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={ariaLabel}
      className={`tdl-popover ${className}`}
      style={{ top: position.top, left: position.left, width }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
};

export default AnchoredPopover;
